import type { CSSProperties } from "react"

// Single source of truth for fee-sticker text layout/sizing, shared by the
// designer's live canvas preview and the actual print renderer
// (feeStickerPrinting.tsx). Previously each had its own copy of this math;
// the designer's copy didn't shrink text the way print did, so a layout that
// looked fine while editing could come out different (clipped/oversized)
// once actually printed. Importing the same functions in both places makes
// that class of drift impossible.

export type StickerLayer = any

export const fitStickerFontSize = (layer: StickerLayer, text: string, scale = 1) => {
  const baseSize = (Number(layer.fontSize) || 12) * scale
  if (layer.autoFit === false || !text) return baseSize
  const width = Math.max(1, (Number(layer.w) || 0) * scale)
  const height = Math.max(1, (Number(layer.h) || 0) * scale)
  const horizontalPadding = 8 * scale
  const usableWidth = Math.max(1, width - horizontalPadding)
  const estimatedWidth = text.length * baseSize * 0.58
  const widthRatio = usableWidth / Math.max(1, estimatedWidth)
  const heightRatio = height / Math.max(1, baseSize * (layer.lineHeight || 1.15))
  const ratio = Math.min(1, widthRatio, heightRatio)
  return Math.max(baseSize * 0.68, baseSize * ratio)
}

export const stickerTextStyle = (layer: StickerLayer, scale = 1, text = ""): CSSProperties => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems:
    layer.verticalAlign === "top" ? "flex-start" : layer.verticalAlign === "bottom" ? "flex-end" : "center",
  justifyContent:
    layer.textAlign === "left" ? "flex-start" : layer.textAlign === "right" ? "flex-end" : "center",
  fontFamily: layer.fontFamily,
  fontSize: fitStickerFontSize(layer, text, scale),
  fontWeight: layer.fontWeight,
  fontStyle: layer.fontStyle,
  color: layer.color,
  textAlign: layer.textAlign,
  lineHeight: layer.lineHeight || 1.15,
  letterSpacing: (layer.letterSpacing || 0) * scale,
  textDecoration: layer.textDecoration,
  whiteSpace: "nowrap",
  padding: 4 * scale,
  overflow: "hidden",
  textOverflow: "clip",
  boxSizing: "border-box",
  transform: layer.textOffsetY ? `translateY(${Number(layer.textOffsetY) * scale}px)` : undefined,
})

export const resolveStickerText = (text: string, vars: Record<string, string>) =>
  String(text || "").replace(/\{\{([^}]+)\}\}/g, (_match, token) => vars[token.trim()] ?? "")

// Realistic sample values so the designer's autofit preview shrinks text the
// same way a real printed sticker would. Previewing with the raw "{{field}}"
// placeholder tokens instead would make short tokens look like they always
// fit, when a real name/value of very different length might actually get
// shrunk or clipped once printed for real.
export const FEE_STICKER_SAMPLE_VARS: Record<string, string> = {
  "student.full_name": "Muhammad Abdul Raheem Siddiqui",
  "student.fathers_name": "Muhammad Abdul Raheem",
  "student.mothers_name": "Hina Abdul Raheem",
  "student.gr_number": "127123",
  "student.class_name": "Class VII",
  "student.section_name": "F",
  "student.roll_number": "24",
  "student.son_or_daughter": "son",
  "student.s_o_or_d_o": "S/O",
  "student.he_or_she": "he",
  "student.his_or_her": "his",
  "student.him_or_her": "him",
  "student.address": "D-54, F.B Area, Karachi",
  "session.name": "Aug 2026-2027",
  "session.full": "Aug 2026-2027",
  "session.short": "26-27",
  "session.mixed": "2026-27",
  "exam_term.name": "Final Term",
  "campus.name": "Naseerabad",
  "campus.code": "NSB",
  "campus.address": "Plot 12, Sector 5, Naseerabad",
  "campus.phone": "021-34567890",
  "school.name": "Metropolitan Academy",
  "school.code": "MPA",
  "fee.monthly": "4,500",
  "fee.tuition": "4,500",
  "fee.st_fund": "300",
  "fee.exam": "500",
  "fee.total": "5,300",
  "fee.sub_total": "5,800",
  "fee.discount": "500",
  "fee.invoice_number": "INV-20261045",
  "fee.month": "August",
  "generated_at": "20/08/2026",
  "batch.ref_number": "REF-2026-0142",
  "cert.ref_number": "CERT-2026-0142",
}
